import type { CardSlot } from '@kenocyber/shared';
import {
  PVP_TEAMS,
  type PvpAbandonCause,
  type PvpEndReason,
  type PvpParticipantId,
  type PvpRuleset,
  type PvpTeam,
  type PvpTeamSlot,
  pvpIsValidSlot,
  pvpParticipantId,
  pvpTeamSlots,
} from '@kenocyber/shared';
import type { CardAbilityState } from '../cards/abilities';
import type { CardEffectState } from '../cards/effects';
import type { AtomicBombSelection } from '../damage/atomic-bomb';
import type { JewelProcSelection } from '../damage/proc-lottery';
import type { PvpRuneKind } from './balance';
import type { PvpCombatProfile } from './combat-profile';
import type { PvpKnockbackState } from './knockback';
import type { PvpShipEffects } from './runes';

/**
 * What the simulation calls a ship, a shot, an event and a result - by identity.
 *
 * ## Why this file exists at all
 *
 * `PvpSimulation` was a PAIR by construction: `ships: [PvpShipState,
 * PvpShipState]`, `this.ships.a`, `this.ships.b`, `otherSeat(projectile.seat)`.
 * Every one of those is a decision made by POSITION - the first element, the
 * `a` key, the other one - and a decision made by position is a decision that
 * silently inherits somebody else's state the first time the collection is
 * built in another order or grows past two.
 *
 * So the vocabulary in `@kenocyber/shared/pvp/participants` (teams, slots and
 * the derived `a1`..`b3` identity) is given a RUNNING shape here: the state a
 * ship carries during a match, the owner a shot remembers, the event a
 * participant produces and the result a TEAM ends with.
 *
 * ## What is deliberately not here
 *
 * No `accountId`. Who is driving a ship is a fact about the lobby, the ticket
 * and the audit trail - the simulation must be re-runnable from a snapshot
 * alone, by a process that has never heard of an account, and a re-simulation
 * that needed one would be a re-simulation that could not be checked offline.
 * `PvpParticipantRef` is where the two are joined, and it lives outside.
 *
 * No seat either. The 1x1's seats are a WIRE fact, converted at the boundary by
 * `legacy-duel.ts` and nowhere else.
 *
 * ## And no order but the canonical one
 *
 * Team `a` before team `b`, then slot ascending. It is the order
 * `pvpParticipantIds` already produces, it is stable across a rebuild, and it
 * is the only tie-break the simulation is allowed to use - so a roster handed
 * to {@link PvpShipRoster.of} shuffled produces exactly the same match as one
 * handed over in order.
 */

/* -------------------------------------------------------------------------- */
/*  Identity                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A chair in a running match: who, on which side, in which slot.
 *
 * Immutable by construction - none of the three ever changes while a match is
 * running, which is what lets everything else be keyed by it.
 */
export interface PvpParticipantSeat {
  readonly participantId: PvpParticipantId;
  readonly team: PvpTeam;
  readonly slot: PvpTeamSlot;
}

/** Every chair a ruleset has, in canonical order. */
export function pvpCanonicalSeats(ruleset: PvpRuleset): readonly PvpParticipantSeat[] {
  const seats: PvpParticipantSeat[] = [];
  for (const team of PVP_TEAMS) {
    for (const slot of pvpTeamSlots(ruleset)) {
      seats.push({ participantId: pvpParticipantId(team, slot), team, slot });
    }
  }
  return seats;
}

/* -------------------------------------------------------------------------- */
/*  The running state                                                         */
/* -------------------------------------------------------------------------- */

/**
 * One ship, mid-match.
 *
 * The same fields the two-seat state carried, with `seat` replaced by the
 * identity triple.
 */
export interface PvpParticipantShipState extends PvpParticipantSeat {
  x: number;
  y: number;
  /** Normalised aim. The renderer turns this into a rotation; nothing else does. */
  aimX: number;
  aimY: number;
  /**
   * **A vida corrente deste casco, em MILLI** (gate V28G.S.1).
   *
   * Zero É a morte - não há um segundo sinalizador, porque duas fontes de
   * verdade para "esta nave ainda está viva" é exatamente como um destroço
   * volta a disparar.
   *
   * ## Porque milli, e porque `bigint`
   *
   * Porque a vida MÁXIMA é milli: ela é a da conta, e nenhuma fonte legal
   * produz pontos inteiros. Uma vida corrente em pontos, comparada contra um
   * teto em milésimos, seria uma barra cujo numerador e denominador falam
   * unidades diferentes.
   *
   * E porque, em milli, o dano CABE. `shotDamageMilli` já devolve milli; agora
   * ele é subtraído diretamente, sem passar por uma conversão. Foi essa
   * conversão - e só ela - que os dois restos abaixo existiam para segurar.
   */
  healthMilli: bigint;
  /**
   * What this hull IS: damage, ceiling, speed, cadence and incorporated hearts.
   *
   * Four of the five used to be module-level constants read at the point of use.
   * Per participant now, so two ships in one room can differ - and since the
   * gate V28F.B one of them makes them differ for real: the count of Primordial
   * Hearts incorporated into the equipped ship. See {@link PvpCombatProfile}.
   */
  readonly profile: PvpCombatProfile;
  /*
   * **`damageCarryMilli` e `healCarryMilli` foram REMOVIDOS** (V28G.S.1).
   *
   * Os dois existiam para uma coisa só: segurar a fração que se perdia ao
   * converter milli-dano e milli-cura numa barra de vida INTEIRA. Com a vida em
   * milli não há conversão nenhuma - o dano é subtraído na unidade em que
   * nasceu, e a cura é somada na mesma - portanto não há resto para segurar.
   *
   * Removê-los não é uma simplificação: mantê-los seria manter dois campos que
   * acumulariam para sempre sem nunca serem gastos, e um estado que não muda
   * nada é um estado que uma instantânea persiste e um servidor compara.
   *
   * {@link lifestealCarryBps} FICA, e a diferença é a razão de ser dele: aquele
   * resto é SUB-MILLI - a fração de um milésimo que uma percentagem produz - e
   * essa continua a não caber em lado nenhum.
   */
  /**
   * O resto SUB-MILLI do Roubo de Vida deste casco, em `[0, 10 000)`
   * (gate V28G.I).
   *
   * A percentagem de um golpe produz uma fração de um MILÉSIMO de ponto de
   * vida. Sem este resto, mil golpes de um milli a 9 % curariam zero em vez de
   * 90 milli - e isso continua verdade com a vida em milli, porque o que ele
   * segura é menor do que a unidade da vida.
   *
   * Vive no ATACANTE e não no alvo nem no par: é cura que ESTE casco ganhou.
   * Largado a zero na morte - um destroço não leva nada para a ronda seguinte.
   */
  lifestealCarryBps: number;
  /**
   * Milli-ticks until this ship may fire again; at or below zero means ready.
   *
   * ## Why it may be negative, and why that is the point
   *
   * It is an accumulator, not a countdown. It decrements by one whole tick
   * while positive - so it can land anywhere in `(-1000, 0]` - and a volley
   * ADDS the interval rather than assigning it, which is what carries the
   * remainder into the next shot. A cadence of 3 670 milli-ticks therefore
   * averages 3,67 ticks between volleys instead of being truncated to 3 or
   * rounded to 4.
   *
   * With the default 4 000 it lands exactly on zero every time and the
   * behaviour is indistinguishable from the integer countdown it replaced.
   */
  cooldownMilliTicks: number;
  /** Movement intent, in `{-1, 0, 1}` per axis. */
  moveX: number;
  moveY: number;
  firing: boolean;
  /** Whether a socket is currently attached. A dropped ship is still a target. */
  connected: boolean;
  /** Highest command sequence applied. Older or repeated ones are dropped. */
  appliedSeq: number;
  /** What this ship is under the influence of, decided entirely by the room. */
  effects: PvpShipEffects;
  /** The destination a player asked for, or `null`. A REQUEST, never a result. */
  teleportTo: { x: number; y: number } | null;
  /**
   * **A ordem de DETONAR que este casco tem em mãos** (gate V28G.N).
   *
   * Latch, como {@link PvpParticipantShipState.teleportTo} é: uma ordem vale um
   * tique e é apagada quando o tique a executa. Um pedido e nunca um resultado -
   * não diz alvo, contagem nem dano, e não há campo onde isso caiba.
   *
   * Estado que decide o FUTURO, e por isso atravessa a instantânea: uma sala
   * reconstruída entre a chegada da ordem e o tique que a consome tem de a
   * executar na mesma. Perdê-la seria engolir uma ação de combate numa
   * reconexão.
   */
  detonateBleed: boolean;
  /**
   * O empurrão que este casco está a sofrer, ou `null` (gate V28G.J).
   *
   * Estado que decide o FUTURO por duas razões independentes, e é por isso que
   * atravessa a instantânea inteiro: diz para onde a nave vai andar sem ninguém
   * lhe tocar, e diz que o teclado do jogador não conta enquanto durar. Uma sala
   * reaberta sem ele devolveria o controlo cedo e deixaria o casco parado a meio
   * de um empurrão que a partida tinha começado.
   *
   * Largado a zero na morte, pela mesma regra dos restos acima: um destroço não
   * leva nada para a ronda seguinte, e um empurrão sobre um casco sem vida seria
   * um destroço a deslizar.
   */
  knockback: PvpKnockbackState | null;
  /**
   * **A FASE da órbita das serras deste casco** (gate V28G.P).
   *
   * Um inteiro em `[0, SAW_ORBIT_PHASE_UNITS)`, avançado uma vez por tique por
   * `sawAdvancePhase`. Zero para todo o casco sem o Diamante Serrilhado, e zero
   * é também a fase inicial de todos os que o têm - determinística e
   * independente da ordem do roster, que é o que o contrato exige.
   *
   * ## Porque a fase é ESTADO e não é derivada do tique
   *
   * Porque ela é derivável do tique **enquanto o nível não mudar**, e o nível
   * pode mudar: na caça uma queda ou uma fusão reconfiguram a sessão no lugar.
   * Uma fase calculada de `tique x velocidade(nível)` daria um salto no instante
   * em que a velocidade mudasse - as quatro serras teletransportavam-se para
   * outra posição da órbita. Acumulada, ela continua exatamente onde estava e
   * passa a andar mais depressa a partir dali.
   *
   * Estado que decide o FUTURO, portanto atravessa a instantânea. É também o que
   * a cena lê para saber onde desenhar cada lâmina: a posição vem da autoridade,
   * e nunca de um relógio local.
   */
  sawPhase: number;
  /**
   * O resto INTEIRO do orçamento das serras deste casco (gate V28G.P).
   *
   * Sempre `< sawDamageDenominator(nível)`, e sempre do DONO das serras - é o
   * orçamento dele que está a ser gasto. Um resto por par serra→alvo faria
   * quatro lâminas contra três inimigos manterem doze contas independentes que
   * nunca somam ao que a nave devia ter feito.
   *
   * Estado que decide o FUTURO, portanto atravessa a instantânea como decimal em
   * string, pela mesma regra de `damageMilli`. Largado a zero na morte, pela
   * mesma regra dos outros restos: um destroço não leva nada para a ronda
   * seguinte.
   */
  sawCarryMilli: bigint;
  /**
   * **Os três espaços de cartas deste casco** (gate V28H.J).
   *
   * Congelados quando o piloto se senta, pela mesma regra das joias e da vida
   * máxima: uma vez, e nunca outra vez. Um espaço vazio simplesmente não produz
   * entrada, portanto a lista tem entre zero e três.
   *
   * Estado que decide o FUTURO - o arrefecimento diz quando a carta pode voltar,
   * e a duração diz o que os canhões estão a fazer agora -, portanto atravessa a
   * instantânea inteira.
   */
  cards: CardAbilityState[];
  /**
   * As ordens de carta já APLICADAS a este casco, as mais recentes primeiro.
   *
   * A deduplicação: uma segunda ordem com a mesma identidade é lida em vez de
   * ligar outra vez. LIMITADA a {@link PVP_CARD_COMMAND_MEMORY}, porque uma lista
   * sem teto seria um cliente a fazer a sala crescer para sempre - e a memória
   * que faz falta é curta, já que uma repetição honesta chega em segundos.
   */
  cardCommands: string[];
  /**
   * A ordem RECEBIDA e ainda não aplicada, ou `null`.
   *
   * Latch e não hold, como a detonação: a sala arma-a quando o frame chega e o
   * tique seguinte consome-a. Uma ordem vale um tique.
   */
  pendingCard: { readonly slot: CardSlot; readonly commandId: string } | null;
  /**
   * A ordem de SOLTAR uma carga, recebida e ainda não aplicada (V28H.N).
   *
   * Latch próprio, e não o mesmo de {@link pendingCard}: um toque na tecla liga
   * e solta no MESMO frame, e um latch partilhado perderia uma das duas.
   */
  pendingCardRelease: { readonly slot: CardSlot; readonly commandId: string } | null;
  /**
   * **A ordem de PILOTAR**, recebida e ainda não aplicada (gate V28H.X).
   *
   * O seu próprio latch, pela mesma razão dos outros dois: embarcar não é ligar
   * nem soltar, e um latch partilhado perderia uma das ordens quando as duas
   * chegassem no mesmo frame.
   *
   * Não sobrevive a uma instantânea, e é deliberado: é uma ordem de um tique, e
   * uma sala reaberta com uma ordem velha embarcaria alguém que carregou no
   * botão antes da reconexão.
   */
  pendingBoard: { readonly commandId: string } | null;
  /**
   * **O que as cartas deixaram a correr sobre este casco** (gate V28H.L).
   *
   * Oito ranhuras - uma por TIPO de penalidade ou benefício - e nunca uma lista.
   * Um tamanho fixo é o que mantém a instantânea de tamanho fixo, e a §1 do
   * equilíbrio diz o que acontece quando duas chegam à mesma ranhura: fica a
   * maior, e empatar renova.
   *
   * Separado de {@link PvpShipEffects}, que é das RUNAS. As duas coisas são
   * temporizadores sobre o mesmo casco e não são a mesma coisa: uma runa
   * apanha-se do chão e é do mapa, uma carta traz-se de casa e é da conta.
   * Juntá-las faria uma Lentidão poder ser recebida de um pad.
   *
   * Estado que decide o FUTURO, portanto atravessa a instantânea inteira.
   */
  cardEffects: CardEffectState;
}

/**
 * Quantas ordens de carta um casco recorda, para as deduplicar.
 *
 * Oito. Uma partida inteira cabe em muito menos do que isso - três espaços com
 * arrefecimentos de dezenas de segundos -, e o número existe para a lista ter um
 * teto e não para ser afinado.
 */
export const PVP_CARD_COMMAND_MEMORY = 8;

/**
 * **O relógio de UM par serra→alvo** (gate V28G.P).
 *
 * ## Porque um por PAR, e não um por serra nem um por alvo
 *
 * Porque o contrato o diz, e porque as duas alternativas produzem jogos
 * diferentes. Um relógio por serra faria a segunda lâmina a tocar o mesmo casco
 * não doer, o que transformaria quatro serras em uma. Um relógio por alvo faria
 * o mesmo. Um por par é o que deixa quatro lâminas a atravessar um casco
 * cobrarem quatro vezes - e o orçamento continua fechado porque o dano de CADA
 * contacto foi dividido por quatro.
 *
 * ## O que ele guarda, e o que ele deliberadamente não guarda
 *
 * O instante do último contacto, no tique da simulação. Não guarda um
 * temporizador a decrementar - ver `sawContactDue` - nem a magnitude, nem o
 * nível, nem quantas vezes já bateu. Tudo isso ou é derivado ou não influencia
 * o futuro.
 */
export interface PvpSawContactState {
  /** Quem tem a serra. O autor de tudo o que ela produzir. */
  readonly ownerParticipantId: PvpParticipantId;
  /** Qual das lâminas da órbita, de `0` a `count - 1`. */
  readonly bladeIndex: number;
  /** Em quem ela bateu. */
  readonly targetParticipantId: PvpParticipantId;
  /** O tique da última instância de dano DESTE par. */
  lastContactTick: number;
}

/**
 * One shot in flight, and who owns it.
 *
 * Both the participant AND the team are carried. They are redundant - the team
 * is derivable from the id - and that is the point: friendly fire is decided by
 * the TEAM on every substep of every shot, and re-deriving it there would mean
 * parsing a string twenty times a second to answer a question the shot already
 * knows the answer to.
 */
export interface PvpParticipantProjectileState {
  readonly id: number;
  readonly ownerParticipantId: PvpParticipantId;
  readonly ownerTeam: PvpTeam;
  /**
   * What this shot will take off, in MILLI-damage, decided when it was FIRED.
   *
   * ## Born with it, and never recomputed
   *
   * The whole temporal contract of the gate V28F.B is this field. A shot that
   * asked the profile at IMPACT would scale with whatever the hull had become
   * in the meantime - a rune that expired, a heart incorporated between the
   * volley and the hit - and the player would watch a projectile change its
   * mind in flight. It is decided once, by `pvpShotDamageMilli`, and carried.
   *
   * ## Why `bigint`, and why milli
   *
   * `bigint` because the count of incorporated hearts has no gameplay ceiling:
   * `40 000 x (10 000 + 100 x 10^40) / 10 000` has forty-two digits, and a
   * `double` would silently round it - an item's effect capped by a type.
   *
   * Milli because a heart makes the value fractional in whole-damage terms and
   * a truncation would delete it. Desde o gate V28G.S.1 a fração não precisa de
   * sobreviver em lado nenhum: a vida do casco é milli também, portanto este
   * `bigint` é subtraído tal e qual - sem conversão e, por isso, sem resto.
   *
   * A snapshot writes it as a DECIMAL STRING, for the same reason a balance is
   * one: see `snapshot-v3.ts`, format version 5.
   */
  readonly damageMilli: bigint;
  /**
   * O proc que este tiro carrega, escolhido quando ele NASCEU (gate V28G.F).
   *
   * `null` para um tiro comum, que é o caso de toda a partida sem joias de
   * proc. Congelado como o dano: um projétil no ar não relê o perfil, portanto
   * trocar de joias a meio não muda o que já foi disparado.
   *
   * Nunca carrega identificador de conta, skin, socket ou instância - ele
   * atravessa o fio para seis clientes.
   */
  readonly jewelProc: JewelProcSelection | null;
  /**
   * A BOMBA que este projétil É, ou `null` para um tiro comum (gate V28G.J).
   *
   * Presente quer dizer que este projétil **é** o Núcleo Atômico: não é um tiro
   * com um extra pendurado, é a entidade em que a salva o transformou. Ele voa
   * mais devagar, deteta por proximidade, detona contra parede e contra o
   * limite, e explode em área - e nada disso é decidido por um segundo campo,
   * é decidido por este ser diferente de `null`.
   *
   * `blastMilli` foi congelado quando a salva nasceu, pela mesma lei que congela
   * `damageMilli` e o corte do Ciclone: um projétil no ar não relê o perfil.
   */
  readonly atomicBomb: AtomicBombSelection | null;
  x: number;
  y: number;
  readonly vx: number;
  readonly vy: number;
  life: number;
  /**
   * **A LINHAGEM deste projétil: nasceu de uma reflexão, ou não** (gate V28G.O).
   *
   * Falso para todo o tiro comum e para toda a bomba que uma salva produziu, que
   * é o caso de toda a partida sem a Égide Espinhosa.
   *
   * ## Quando é verdadeiro, e porque é só a Bomba
   *
   * Só uma bomba refletida chega aqui com `true`. Um tiro comum intercetado
   * **não produz projétil nenhum**: o §5 do gate manda que o original termine no
   * contacto e que o dano congelado seja aplicado ao atacante imediatamente, com
   * um feixe branco no lugar de um objeto a viajar. Uma bomba é diferente porque
   * ela É uma entidade - com raio, empurrão, colisão e explosão - e o contrato
   * manda que uma equivalente **nasça na nave refletora** e parta dali.
   *
   * ## O que ele desliga
   *
   * A explosão desta bomba é descendente de um payload refletido, portanto nem a
   * proximidade nem o splash podem ser refletidos outra vez - pela TABELA de
   * `damage/reflection.ts`, e não por um contador. E o dano DIRETO dela deixa de
   * entrar com origem `cannon`: uma bomba refletida não é o tiro de ninguém, e
   * origem `jewel` é o que lhe nega proc, Roubo de Vida e estado.
   */
  readonly reflected: boolean;
  /**
   * **A explosão de uma CARTA que este projétil carrega**, ou `null`
   * (gate V28H.L).
   *
   * A Esfera Ígnea e o Catolé. Elas não são a Bomba Atômica da joia: trazem o
   * seu próprio raio e o seu próprio dano, e o dano nasce com origem `card` -
   * portanto não activa joia nenhuma, que é o contrato do catálogo.
   *
   * Separado de `atomicBomb` de propósito. A Bomba é uma rolagem de salva com
   * uma magnitude congelada e um raio de equilíbrio partilhado; isto é uma
   * habilidade com números próprios. Juntá-las obrigaria a um campo "de quem
   * és tu" dentro da que sobrasse, e é exactamente assim que uma regra de uma
   * passa a valer para a outra sem ninguém decidir.
   */
  /**
   * **Este tiro saiu de uma ESTRUTURA, e não de um casco** (gate V28H.X).
   *
   * Ausente é o caso de sempre. Ele existe para a cena: os seis canhões do
   * Leviatã disparam vermelho néon - decisão do dono - e a cor de um tiro sai
   * hoje da cadeira de quem o deu, que aqui é a do piloto. Sem este campo, a
   * salva do helicóptero saía da cor da nave que está lá dentro e escondida.
   *
   * O TIPO da estrutura e não a identidade dela: quem desenha precisa de saber
   * de qual - a salva do Leviatã é vermelha e a do Drone não, e só uma delas
   * dispara à velocidade de ataque de alguém - e um id abriria a porta à cena
   * procurar coisas.
   *
   * **Não viaja na instantânea**, e é uma escolha: uma sala reaberta a meio de
   * um voo perde a cor de uma salva que ainda esteja no ar - por menos de um
   * segundo, que é o que um tiro vive - e isso não vale um degrau de formato.
   */
  readonly fromSummon?: 'drone' | 'leviathan';
  readonly cardBlast: {
    /**
     * **Qual carta este corpo é** (gates V28H.U e V28H.W).
     *
     * As duas primeiras partilhavam tudo menos os números, e a explosão delas
     * era a mesma da Bomba Atômica. O tipo é o que permite a cada uma ter a sua
     * arte, o seu empurrão e o seu tamanho desenhado - sem a cena adivinhar pelo
     * raio, que seria decidir a arte a partir de um valor de equilíbrio.
     *
     * As três últimas não rebentam: são dardos - dois de gelo (gate V28H.W) e um
     * de energia elétrica (gate V28H.X) - que aplicam um efeito a quem tocam.
     * Viajam nesta mesma lista porque são a mesma coisa mecânica - um corpo que
     * a sala move e que colide - e o que muda é o que acontece na chegada.
     */
    readonly kind: 'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt';
    readonly radiusMu: number;
    readonly damageMilli: bigint;
  } | null;
  /**
   * **Quem este projétil PERSEGUE**, ou `null` para um que voa a direito.
   *
   * Só o Catolé, que é "teleguiada" por descrição aprovada. A perseguição é
   * uma correcção de rumo por tique, limitada, e o alvo é escolhido pelo
   * servidor no instante do lançamento - nunca renegociado, porque um projétil
   * que trocasse de alvo a meio seria um projétil que ninguém consegue evitar.
   */
  readonly homingTargetId: PvpParticipantId | null;
}

/**
 * Presentation only, drained after each tick, and addressed by participant.
 *
 * The team travels beside the id for the same reason it does on a projectile:
 * a renderer colouring by side should not have to parse an identity to find
 * one, and a parse is a place a wrong answer can be invented.
 */
export type PvpParticipantEvent =
  /**
   * Um proc de joia ACONTECEU (gate V28G.F).
   *
   * O mínimo para uma apresentação futura, e **sem consumidor visual**: diz o
   * quê, de quem, em quem e onde. O dano continua a viajar no evento `hit` que
   * já existe.
   *
   * Não carrega magnitude, `levelSum`, cópias, identificador de inventário, nem
   * cor, textura ou escala. `projectileId` está aqui para uma apresentação
   * poder DEDUPLICAR num replay, e não para desenhar.
   */
  | {
      /*
       * `proc`, e não `jewel_proc`, desde o gate V28G.H.
       *
       * O nome é o mesmo nos dois modos - a caça emite `type: 'proc'` com os
       * mesmos campos - e isso é metade da razão. A outra metade é a guarda do
       * renderer: `jewel-presentation.test.ts` recusa qualquer ficheiro que
       * fale de joias E escolha arte, portanto um evento chamado `jewel_proc`
       * seria um evento que nenhuma cena pode consumir sem quebrar a regra que
       * impede as passivas de decorarem um tiro.
       *
       * O que a cena precisa de saber é o EFEITO, e `procId` di-lo. Que joia o
       * originou é uma coisa que ela não precisa - e não pode - saber.
       */
      readonly kind: 'proc';
      readonly procId: string;
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      readonly targetId: PvpParticipantId;
      readonly projectileId: number;
      /**
       * **O tique da SIMULAÇÃO em que este evento nasceu** (gate V28G.I).
       *
       * ## Porque não chega o tique do frame
       *
       * Porque não é o mesmo número. A sala simula a 20 Hz e publica à cadência
       * de envio do cliente, portanto um frame carrega MAIS DE UM TIQUE - está
       * escrito em `room.ts`, ao lado da caixa de saída que existe por causa
       * disso. O `k` do frame diz quando ele foi PUBLICADO; isto diz quando o
       * facto ACONTECEU, e os dois divergem sempre que um frame junta dois
       * tiques.
       *
       * ## Para que serve
       *
       * É a terceira metade da chave de consolidação da apresentação:
       * `(atacante, alvo, tique)`. Dez acertos no mesmo alvo no mesmo tique são
       * um sinal; dois tiques são dois sinais, mesmo entregues no mesmo frame.
       * Sem ele, uma cena só poderia agrupar pelo lote - que é mais grosseiro
       * que o tique - ou por relógio local, que não é autoritativo.
       *
       * ## O que ele NÃO é
       *
       * Apresentação, e só. Não entra em dano, cura, resto, RNG, instantânea
       * mecânica, economia ou impressão digital. Nenhum deles tem por onde o
       * receber.
       */
      readonly tick: number;
      /**
       * **A vida REALMENTE acrescentada ao atacante, em milli** (gate V28G.I).
       *
       * ## Porque o evento a carrega
       *
       * Porque o cliente não pode recalculá-la. Ele não tem o perfil, não tem o
       * nível, não tem a percentagem, e a diferença observada na barra de vida
       * não serve: ela move-se por dano, por regeneração e por cura ao mesmo
       * tempo, e nenhuma delas é distinguível de fora. O único que sabe quanto
       * a cura pôs é quem a pôs.
       *
       * ## Porque em MILLI, e inteiro
       *
       * Porque a apresentação SOMA todas as curas do mesmo atacante no mesmo
       * tique antes de mostrar um número, e somar floats acumularia erro. Em
       * milli inteiro a soma é exata e a divisão acontece uma vez, ao formatar.
       *
       * ## O que ela já é
       *
       * O valor DEPOIS do clamp da vida máxima. Vida cheia dá zero, e um golpe
       * cuja cura ficou toda no resto dá zero - e nenhum dos dois produz evento,
       * portanto este campo nunca chega a zero por essas vias.
       *
       * Ausente para um efeito que não cura: o corte do Ciclone não tem uma
       * magnitude de cura, e um zero ali seria "curou nada" em vez de "não é uma
       * cura".
       */
      readonly healedMilli?: bigint;
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly kind: 'hit';
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      readonly x: number;
      readonly y: number;
      /**
       * O dano REALMENTE aplicado, em MILLI (gate V28G.S.1).
       *
       * Depois do clamp do overkill, e na unidade em que a vida vive - portanto
       * este numero e a barra falam a mesma lingua, e um golpe de 40,4 imprime
       * `40,4` em vez de `40` com um resto guardado noutro sitio.
       *
       * `bigint` porque o dano nao tem tecto: um casco com coracoes
       * incorporados produz magnitudes de dezenas de digitos.
       */
      readonly amount: bigint;
    }
  | {
      readonly kind: 'fired';
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly kind: 'death';
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly kind: 'rune';
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      readonly rune: PvpRuneKind;
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly kind: 'teleport';
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      readonly x: number;
      readonly y: number;
    }
  /**
   * Uma EXPLOSÃO aconteceu (gate V28G.J).
   *
   * ## Porque não chega o evento `proc`
   *
   * Porque uma explosão tem um CENTRO e um RAIO, e o `proc` tem um alvo. O
   * Ciclone corta um casco e o número que importa é "onde bateu"; a Bomba abre
   * um círculo, e uma detonação contra uma parede não tem alvo nenhum - o
   * `targetId` do `proc` não teria o que lá pôr, e um id inventado seria uma
   * mentira que a cena desenharia.
   *
   * ## O raio VIAJA, e é o raio MECÂNICO
   *
   * Porque o §5 exige que a onda de choque desenhada represente o raio real. Uma
   * cena que soubesse o raio por si teria uma segunda cópia dele, e o dia em que
   * uma delas mudasse seria o dia em que o círculo deixava de dizer a verdade.
   *
   * Não carrega magnitude, nível, cópias, joia nem identificador de inventário.
   * `projectileId` está aqui para uma apresentação poder DEDUPLICAR num replay.
   */
  | {
      readonly kind: 'blast';
      /** O EFEITO, nunca a joia: `atomic_blast`. */
      readonly effectId: string;
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      readonly projectileId: number;
      /** O tique da SIMULAÇÃO em que a bomba detonou. Nunca o de publicação. */
      readonly tick: number;
      /** O centro, em unidades de mundo. */
      readonly x: number;
      readonly y: number;
      /** O raio MECÂNICO, o mesmo que decidiu quem foi atingido. */
      readonly radius: number;
      /** Quem apanhou, para a cena poder piscar exatamente esses cascos. */
      readonly hit: readonly PvpParticipantId[];
    }
  /**
   * **Uma DETONAÇÃO de sangramento aconteceu** (gate V28G.N).
   *
   * ## Porque não chega o estado, e porque não chega o `proc`
   *
   * O estado não chega porque as marcas desaparecerem é ambíguo: elas
   * desaparecem quando são detonadas, quando o alvo morre e quando o autor
   * morre. Uma cena que inferisse a detonação de "a lista encolheu" acenderia um
   * corte carmesim sobre um casco que apenas foi abatido.
   *
   * O `proc` não chega porque ele deduplica por PROJÉTIL, e uma detonação não
   * tem projétil nenhum - a que a semeou já não existe há muito. A chave aqui é
   * `(autor, tique)`: um autor detona no máximo uma vez por tique, por
   * construção, porque a ordem é consumida antes de qualquer aplicação.
   *
   * ## Um evento por DETONAÇÃO, e não um por alvo
   *
   * Porque a ordem é uma só e o §"todas as marcas daquele jogador detonam no
   * mesmo tique" é sobre ela. Os alvos vêm dentro, cada um com quantas marcas
   * SUAS rebentaram - que é o que deixa a intensidade do efeito refletir a
   * quantidade sem a cena ter de contar marcas que já saíram do estado.
   *
   * Não carrega magnitude, nível, cópias, joia nem identificador de inventário.
   * O dano viaja no `hit` que já existe, com o valor realmente aplicado.
   */
  | {
      readonly kind: 'bleed';
      /** O EFEITO, nunca a joia: `bleed_detonation`. */
      readonly effectId: string;
      /** Quem mandou. As marcas eram dele, e só dele. */
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      /** O tique da SIMULAÇÃO em que a ordem foi executada. */
      readonly tick: number;
      /** Um alvo detonado, e quantas marcas deste autor rebentaram nele. */
      readonly targets: readonly {
        readonly targetId: PvpParticipantId;
        readonly marks: number;
        /**
         * O dano REALMENTE aplicado a este casco, em MILLI (gate V28G.S.1).
         *
         * Depois do clamp do overkill, e nunca a soma bruta das marcas. Viaja
         * porque o cliente não o pode recalcular - ele não tem as magnitudes
         * congeladas - e porque o duelo passou a imprimir um número por
         * detonação, que é a única coisa que o lê.
         */
        readonly applied: bigint;
      }[];
    }
  /**
   * **Uma REFLEXÃO aconteceu** (gate V28G.O).
   *
   * ## Porque não chega nenhum dos quatro canais que já existem
   *
   * Um `proc` deduplica pelo PROJÉTIL que o trouxe, e uma reflexão pode
   * intercetar uma parcela de veneno que nenhum projétil trouxe. Um `blast` tem
   * um centro e um raio, e uma reflexão não tem área nenhuma. Uma detonação
   * acontece em vários cascos por uma ordem, e uma reflexão tem exatamente dois
   * intervenientes. E um `hit` diz que doeu, sem dizer porquê.
   *
   * Mas a razão decisiva é outra: uma reflexão precisa de **duas posições**. O
   * brilho percorre a silhueta do refletor e o feixe termina no atacante, e um
   * evento com um ponto só obrigaria a cena a ir buscar o outro ao estado - que
   * num frame publicado mais tarde já não é o mesmo ponto.
   *
   * ## Dois estágios, e nenhum é derivável do outro
   *
   * - `intercepted` — a autoridade confirmou a interceção. É isto que acende o
   *   brilho, e é isto que desenha o feixe quando o payload era um tiro comum.
   *   Acontece **sempre**, mesmo quando nada chega a doer: um míssil refletido
   *   pode ir bater numa pilastra e nunca ferir ninguém, e o jogador tem de ver
   *   que defendeu na mesma;
   * - `landed` — o payload redirecionado causou dano. É isto que imprime
   *   `REFLECT` sobre o número, e o `applied` que ele traz é o dano REAL depois
   *   do clamp do overkill, que o cliente não tem como recalcular: ele não tem
   *   as magnitudes congeladas, e a barra move-se por tiros do mesmo tique.
   *
   * Para um tiro comum os dois saem no mesmo tique, porque a resolução é
   * imediata. Para o Míssil e para a Bomba o `landed` chega segundos depois, e é
   * exatamente o §7 do gate: `REFLECT` aparece quando o novo payload finalmente
   * causa dano, e não no relançamento.
   *
   * ## A chave de deduplicação
   *
   * `(participantId, targetId, tick, payload, stage)`. Um casco interceta no
   * máximo um payload de cada tipo por tique contra o mesmo adversário, porque
   * cada instância hostil consome a sua rolagem uma vez.
   *
   * Não carrega magnitude congelada, nível, cópias, joia nem identificador de
   * inventário. `payload` é o TIPO de efeito intercetado e nunca a peça que o
   * emitiu - `atomic_bomb_proximity`, e nunca `jewel_atomic_bomb`.
   */
  | {
      readonly kind: 'reflect';
      /** O EFEITO, nunca a joia: `reflection`. */
      readonly effectId: string;
      /** O REFLETOR, e o novo autor do payload. */
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      /** Quem recebe o payload redirecionado: o atacante original. */
      readonly targetId: PvpParticipantId;
      /** O tique da SIMULAÇÃO. Nunca o de publicação. */
      readonly tick: number;
      /** Que instância hostil foi intercetada. Um {@link HostilePayloadKind}. */
      readonly payload: string;
      /** `intercepted` acende o brilho; `landed` imprime o número. */
      readonly stage: 'intercepted' | 'landed';
      /** O refletor, em MILLI-unidades: onde o brilho corre. */
      readonly x: number;
      readonly y: number;
      /** O destinatário: onde o feixe acaba, e onde o número é impresso. */
      readonly tx: number;
      readonly ty: number;
      /**
       * O dano REALMENTE aplicado por este redirecionamento, em MILLI.
       *
       * Presente **só** em `landed`, e ausente em `intercepted` - onde ainda não
       * há dano nenhum a declarar. Um zero ali seria "não doeu" em vez de "ainda
       * não aconteceu", e as duas coisas são diferentes.
       */
      readonly applied?: bigint;
    };

/**
 * How a match ended, as the simulation knows it: a TEAM won, or nobody did.
 *
 * Not a `PvpOutcome` and not a `PvpResult`. `PvpOutcome` is the vocabulary the
 * database and the history table speak and carries no tick count;
 * `PvpResult` names a SEAT, which three ships a side do not have. This is the
 * internal fact both are derived from, and `legacy-duel.ts` is where the
 * derivation for the shipped 1x1 lives.
 */
export interface PvpTeamResult {
  /** `null` for every draw and for every result nobody should trust. */
  readonly winner: PvpTeam | null;
  readonly reason: PvpEndReason;
  /** Ticks the simulation actually advanced. Zero for a match that never ran. */
  readonly ticks: number;
  /**
   * Why an abandonment was an abandonment, and `null` for every other ending.
   *
   * REQUIRED, deliberately. A persisted record may lack it - storage outlives
   * the code that wrote it - but a result this build DECIDES must always have
   * chosen, and an optional field here is a field the next ending forgets. The
   * absence of an abandonment is written down as `null` rather than left out.
   */
  readonly abandonment: PvpAbandonment | null;
}

/**
 * Who abandoned, and how, in the identity the simulation speaks.
 *
 * A PARTICIPANT, never a seat and never a team: three ships a side means the
 * chair is the only thing that identifies one person, and a team letter would
 * make "my team-mate left" and "I left" the same fact. `legacy-duel.ts` converts
 * to a seat for the 1x1 wire, and refuses for anything that has no seat.
 *
 * No account id, for the same reason nothing else in this file has one: the
 * simulation must be re-runnable from a snapshot by a process that has never
 * heard of an account.
 */
export interface PvpAbandonment {
  readonly cause: PvpAbandonCause;
  readonly participantId: PvpParticipantId;
}

/* -------------------------------------------------------------------------- */
/*  The collection                                                            */
/* -------------------------------------------------------------------------- */

/** Every way a roster can be wrong. One code per rule, and all of them refuse. */
export type PvpRosterRefusal =
  | 'slot_not_in_ruleset'
  | 'duplicate_participant_id'
  | 'duplicate_team_slot'
  | 'participant_id_mismatch'
  | 'roster_incomplete';

/**
 * A roster this build refuses to simulate.
 *
 * A `RangeError` so the existing `restore` contract - "a malformed snapshot
 * must fail loudly" - is unchanged, with a machine `code` on top so a caller
 * can tell a duplicate from a missing slot without reading prose.
 */
export class PvpRosterError extends RangeError {
  override readonly name = 'PvpRosterError';

  constructor(
    readonly code: PvpRosterRefusal,
    message: string,
  ) {
    super(message);
  }
}

/**
 * The ships of one match, keyed by identity and iterated in canonical order.
 *
 * ## Why a class and not a `Map`
 *
 * Because the invariants are the point. A `Map` would let a caller build a
 * duel with two `a1`s, a 1x1 with a slot 3, or a 3x3 missing `b2` - and each of
 * those produces a match that runs, which is worse than one that refuses. The
 * checks happen once, at construction, and everything downstream may then
 * assume the roster is exactly the ruleset's.
 *
 * ## What "canonical" buys
 *
 * {@link PvpShipRoster.all} returns the same order however the ships arrived,
 * so nothing the simulation does can depend on how a lobby happened to fill an
 * array. Where a rule needs a tie-break - two ships equidistant from a pad, two
 * enemies equidistant from a shot - {@link PvpShipRoster.indexOf} is the only
 * one allowed, and it is a property of the identity rather than of the array.
 */
export class PvpShipRoster {
  private readonly byId: ReadonlyMap<PvpParticipantId, PvpParticipantShipState>;
  private readonly ordered: readonly PvpParticipantShipState[];
  private readonly order: ReadonlyMap<PvpParticipantId, number>;
  private readonly byTeam: ReadonlyMap<PvpTeam, readonly PvpParticipantShipState[]>;
  private readonly enemiesByTeam: ReadonlyMap<PvpTeam, readonly PvpParticipantShipState[]>;

  private constructor(
    readonly ruleset: PvpRuleset,
    ordered: readonly PvpParticipantShipState[],
  ) {
    this.ordered = ordered;
    this.byId = new Map(ordered.map((ship) => [ship.participantId, ship]));
    this.order = new Map(ordered.map((ship, index) => [ship.participantId, index]));
    const teams = new Map<PvpTeam, PvpParticipantShipState[]>();
    const enemies = new Map<PvpTeam, PvpParticipantShipState[]>();
    for (const team of PVP_TEAMS) {
      teams.set(
        team,
        ordered.filter((ship) => ship.team === team),
      );
      /*
       * Computed ONCE, not per shot.
       *
       * A shot asks who it may hit on every substep of every tick, and the
       * answer never changes while a match runs - the roster is fixed at
       * construction and a dead ship stays in it. Rebuilding the list there
       * would allocate ninety arrays a tick to say the same thing.
       */
      enemies.set(
        team,
        ordered.filter((ship) => ship.team !== team),
      );
    }
    this.byTeam = teams;
    this.enemiesByTeam = enemies;
  }

  /**
   * Builds a roster, or refuses.
   *
   * The argument order is IGNORED - the result is canonical either way - which
   * is exactly what makes "a shuffled collection produces the same state" a
   * property of the type rather than a hope about its callers.
   */
  static of(ruleset: PvpRuleset, ships: readonly PvpParticipantShipState[]): PvpShipRoster {
    const seenIds = new Set<PvpParticipantId>();
    const seenChairs = new Set<string>();
    for (const ship of ships) {
      if (!pvpIsValidSlot(ruleset, ship.slot)) {
        throw new PvpRosterError('slot_not_in_ruleset', `${ruleset} has no slot ${ship.slot}`);
      }
      if (seenIds.has(ship.participantId)) {
        throw new PvpRosterError(
          'duplicate_participant_id',
          `participant ${ship.participantId} appears twice`,
        );
      }
      seenIds.add(ship.participantId);
      const chair = `${ship.team}:${ship.slot}`;
      if (seenChairs.has(chair)) {
        throw new PvpRosterError(
          'duplicate_team_slot',
          `team ${ship.team} slot ${ship.slot} was handed out twice`,
        );
      }
      seenChairs.add(chair);
      if (ship.participantId !== pvpParticipantId(ship.team, ship.slot)) {
        /*
         * The id is DERIVED from the pair, so one that disagrees with it is not
         * a naming choice - it is two different answers to "who is this", and
         * whichever one a later reader trusts, the other becomes a ship somebody
         * else can move.
         */
        throw new PvpRosterError(
          'participant_id_mismatch',
          `${ship.participantId} is not team ${ship.team} slot ${ship.slot}`,
        );
      }
    }
    const canonical = pvpCanonicalSeats(ruleset);
    const ordered: PvpParticipantShipState[] = [];
    for (const seat of canonical) {
      const ship = ships.find((candidate) => candidate.participantId === seat.participantId);
      if (ship === undefined) {
        throw new PvpRosterError(
          'roster_incomplete',
          `${ruleset} needs ${seat.participantId} and the roster has no such ship`,
        );
      }
      ordered.push(ship);
    }
    if (ordered.length !== ships.length) {
      // Unreachable given the duplicate checks above; asserted rather than
      // assumed, because "every ship was used" is what makes the canonical
      // array a reordering rather than a filter.
      throw new PvpRosterError(
        'roster_incomplete',
        `${ruleset} was given ships it has no slot for`,
      );
    }
    return new PvpShipRoster(ruleset, ordered);
  }

  get size(): number {
    return this.ordered.length;
  }

  /** Everyone, team then slot. The only iteration order the simulation uses. */
  all(): readonly PvpParticipantShipState[] {
    return this.ordered;
  }

  has(participantId: PvpParticipantId): boolean {
    return this.byId.has(participantId);
  }

  /** The ship, or `undefined`. A caller that must have one uses {@link require}. */
  get(participantId: PvpParticipantId): PvpParticipantShipState | undefined {
    return this.byId.get(participantId);
  }

  /** The ship, or a refusal. Never a silently absent one. */
  require(participantId: PvpParticipantId): PvpParticipantShipState {
    const ship = this.byId.get(participantId);
    if (ship === undefined) {
      throw new PvpRosterError(
        'roster_incomplete',
        `no participant ${participantId} in this match`,
      );
    }
    return ship;
  }

  /** One side, in slot order. */
  team(team: PvpTeam): readonly PvpParticipantShipState[] {
    return this.byTeam.get(team) ?? [];
  }

  /**
   * Everyone a ship of this team may legitimately hit.
   *
   * The whole of "no friendly fire" and "a shot never hits its owner", as one
   * question asked once per shot rather than a pair of comparisons repeated at
   * every substep.
   */
  enemiesOf(team: PvpTeam): readonly PvpParticipantShipState[] {
    return this.enemiesByTeam.get(team) ?? [];
  }

  /** Where this participant sits in canonical order. The only allowed tie-break. */
  indexOf(participantId: PvpParticipantId): number {
    return this.order.get(participantId) ?? -1;
  }
}
